import {
  DayPost,
  TrainingLog,
  HealthLog,
  WellnessLog,
  RecoveryLog,
  NiggleLog,
} from "../types/post";
import { generateDateID } from "../services/dayPostService";
import {
  calcSleepWithWellnessFallback,
  formatSleepDuration,
} from "./sleepUtils";
import peakAIRules from "./peakAIRules.json";

// Garmin health logs carry `provider: "GARMIN"` (direct integration, Terra
// since provider was stored, and manual logs a Garmin night was merged into).
// Older logs are only tagged in `notes`: Terra's "Imported from GARMIN" or the
// direct writer's "Automatically added from Garmin device".
const GARMIN_IMPORT_NOTE = /(imported|added) from\s+garmin\b/i;

export const GARMIN_AI_EXCLUSION_NOTE =
  "We do not have access to this data due to Garmin's policies. Do not infer, estimate, or comment on Garmin sleep, heart rate variability, resting heart rate, or respiratory rate.";

export const isGarminImportedHealthLog = (
  log: { notes?: string | null; provider?: string | null } | null | undefined,
): boolean =>
  log?.provider?.toUpperCase() === "GARMIN" || GARMIN_IMPORT_NOTE.test(log?.notes ?? "");

const healthLogHasCardioMetrics = (log: {
  hrv?: number | null;
  rhr?: number | null;
  respRate?: number | null;
}): boolean =>
  (!!log.hrv && log.hrv > 0) ||
  (!!log.rhr && log.rhr > 0) ||
  (!!log.respRate && log.respRate > 0);

const healthLogHasSleep = (log: {
  sleepDuration?: number | null;
  sleepTimeline?: unknown[] | null;
}): boolean =>
  (!!log.sleepDuration && log.sleepDuration > 0) ||
  (!!log.sleepTimeline && log.sleepTimeline.length > 0);

// Peak AI Rules interface
interface PeakAIRule {
  alertId: string;
  condition: string;
  alertTier: "Red" | "Amber" | "Green";
  primaryTriggerDriver: string;
  secondaryTriggerDriver: string;
  alertName: string;
  meaning?: string;
  athleteInsight: string;
}

// Function to get all Peak AI Rules
export const getPeakAIRules = (): PeakAIRule[] => {
  return peakAIRules as PeakAIRule[];
};

// Function to get a specific rule by ID
export const getPeakAIRuleById = (ruleId: string): PeakAIRule | undefined => {
  const rules = getPeakAIRules();
  return rules.find((rule) => rule.alertId === ruleId);
};

// Function to get rules by traffic light color
export const getPeakAIRulesByAlertTier = (
  alertTier: "Red" | "Amber" | "Green",
): PeakAIRule[] => {
  const rules = getPeakAIRules();
  return rules.filter((rule) => rule.alertTier === alertTier);
};

// Additional context for rule evaluation (cycle phase, injuries, trend flags, etc.)
interface RuleContext {
  // Cycle
  cyclePhase?: string;
  isMenstruatingFemale?: boolean;
  sleepRating?: number;
  stressRating?: number;
  // Health
  injured?: boolean;
  canTrain?: boolean;
  sickFactor?: boolean;
  hasNiggle?: boolean;
  // TE trend flags — computed from dayPosts before evaluation
  consecutiveExtremeLoadDays?: boolean;    // TE-01
  extremeLoadSpikeYesterday?: boolean;     // TE-02
  daysSinceLastSession?: number;           // TE-03 (>=4 triggers)
  soreness3Days?: boolean;                 // TE-04
  physicalScore3Days?: boolean;            // TE-05
  recoveryScore3Days?: boolean;            // TE-06
  weeklyLoadIncrease4Weeks?: boolean;      // TE-09
  sleepQuality3Days?: boolean;             // TE-10
  mood3Days?: boolean;                     // TE-11
  stress3Days?: boolean;                   // TE-12
  health3Days?: boolean;                   // TE-13
  loadBelowBaseline?: boolean;             // TE-14
  hlrTrending4Days?: boolean;              // TE-15
  peakScoreTrending4Days?: boolean;        // TE-16
}

// Rule group identifier from alertId prefix
type RuleGroup = "TE" | "CP" | "T3" | "T4" | "T5";

const getRuleGroup = (alertId: string): RuleGroup => {
  if (alertId.startsWith("TE")) return "TE";
  if (alertId.startsWith("CP")) return "CP";
  if (alertId.startsWith("T3")) return "T3";
  if (alertId.startsWith("T4")) return "T4";
  return "T5";
};

// Priority rank within each colour tier (lower = higher priority)
const RED_PRIORITY: Record<RuleGroup, number> = {
  TE: 1, CP: 2, T3: 3, T4: 4, T5: 5,
};
const AMBER_PRIORITY: Record<RuleGroup, number> = {
  T3: 1, CP: 2, TE: 3, T4: 4, T5: 5,
};
const GREEN_PRIORITY: Record<RuleGroup, number> = {
  T3: 1, CP: 2, TE: 3, T4: 4, T5: 5,
};

// Up to 3 prioritised alerts selected per the firing logic spec
export interface AlertSelection {
  alerts: PeakAIRule[];                // 1–3 alerts to display
  displayCombination: string;          // e.g. "2 Red + 1 Amber"
  primaryTone: "Red" | "Amber" | "Green";
}

// Parse and evaluate a single clause like "workloadFactor 0.90–1.00" or "HLR >1.03"
const evaluateClause = (
  clause: string,
  variables: Record<string, number | string | boolean>,
): boolean => {
  const trimmed = clause.trim();

  // Handle string equality: "cyclePhase=ovulatory"
  const eqMatch = trimmed.match(/^(\w+)\s*=\s*(.+)$/);
  if (eqMatch) {
    const [, varName, expected] = eqMatch;
    const actual = variables[varName];
    if (actual === undefined) return false;
    // Boolean comparison
    if (expected === "true") return actual === true;
    if (expected === "false") return actual === false;
    // String comparison
    return String(actual) === expected;
  }

  // Handle IN operator: "cyclePhase IN (early_luteal,late_luteal)"
  const inMatch = trimmed.match(/^(\w+)\s+IN\s+\(([^)]+)\)$/);
  if (inMatch) {
    const [, varName, valueList] = inMatch;
    const actual = variables[varName];
    if (actual === undefined) return false;
    const allowed = valueList.split(",").map((v) => v.trim());
    return allowed.includes(String(actual));
  }

  // Handle rating comparisons: "sleepRating ≥6/7" -> compare sleepRating >= 6
  const ratingMatch = trimmed.match(
    /^(\w+)\s*([<>]=?|[≤≥])\s*(\d+)\/\d+$/,
  );
  if (ratingMatch) {
    const [, varName, op, valueStr] = ratingMatch;
    const actual = variables[varName];
    if (actual === undefined || typeof actual !== "number") return false;
    const threshold = parseFloat(valueStr);
    return compareOp(actual, op, threshold);
  }

  // Handle range: "workloadFactor 0.90–1.00" or "workloadFactor 0.90--1.00"
  const rangeMatch = trimmed.match(
    /^(\w+)\s+(\d+(?:\.\d+)?)\s*(?:--|\u2013|\u2014|-)\s*(\d+(?:\.\d+)?)$/,
  );
  if (rangeMatch) {
    const [, varName, minStr, maxStr] = rangeMatch;
    const actual = variables[varName];
    if (actual === undefined || typeof actual !== "number") return false;
    return actual >= parseFloat(minStr) && actual <= parseFloat(maxStr);
  }

  // Handle comparison: "workloadFactor >1.25" or "HLR <0.93"
  const compMatch = trimmed.match(
    /^(\w+)\s*([<>]=?|[≤≥])\s*(\d+(?:\.\d+)?)$/,
  );
  if (compMatch) {
    const [, varName, op, valueStr] = compMatch;
    const actual = variables[varName];
    if (actual === undefined || typeof actual !== "number") return false;
    return compareOp(actual, op, parseFloat(valueStr));
  }

  console.warn(`Could not parse clause: "${trimmed}"`);
  return false;
};

const compareOp = (actual: number, op: string, threshold: number): boolean => {
  switch (op) {
    case ">":
      return actual > threshold;
    case "<":
      return actual < threshold;
    case ">=":
    case "≥":
      return actual >= threshold;
    case "<=":
    case "≤":
      return actual <= threshold;
    default:
      return false;
  }
};

// Function to evaluate a rule condition against athlete data
const evaluateRuleCondition = (
  condition: string,
  variables: Record<string, number | string | boolean>,
): boolean => {
  try {
    // Split on AND, evaluate each clause
    const clauses = condition.split(/\bAND\b/);
    return clauses.every((clause) => evaluateClause(clause, variables));
  } catch (error) {
    console.warn(`Failed to evaluate rule condition: ${condition}`, error);
    return false;
  }
};

// Evaluate a single TE rule by its alertId using pre-computed context flags
const evaluateTERule = (
  alertId: string,
  context: RuleContext,
): boolean => {
  switch (alertId) {
    case "TE-01": return context.consecutiveExtremeLoadDays === true;
    case "TE-02": return context.extremeLoadSpikeYesterday === true;
    case "TE-03": return (context.daysSinceLastSession ?? 0) >= 4;
    case "TE-04": return context.soreness3Days === true;
    case "TE-05": return context.physicalScore3Days === true;
    case "TE-06": return context.recoveryScore3Days === true;
    case "TE-07": return context.injured === true && context.canTrain === false;
    case "TE-08": return context.sickFactor === true;
    case "TE-09": return context.weeklyLoadIncrease4Weeks === true;
    case "TE-10": return context.sleepQuality3Days === true;
    case "TE-11": return context.mood3Days === true;
    case "TE-12": return context.stress3Days === true;
    case "TE-13": return context.health3Days === true;
    case "TE-14": return context.loadBelowBaseline === true;
    case "TE-15": return context.hlrTrending4Days === true;
    case "TE-16": return context.peakScoreTrending4Days === true;
    case "TE-17": return context.hasNiggle === true;
    case "TE-18": return context.injured === true && context.canTrain === true;
    default: return false;
  }
};

// Evaluate all rules and return every rule whose condition fires
const evaluateAllRules = (
  workloadFactor: number,
  peakScore: number,
  wellnessFinal: number,
  hlr: number,
  context: RuleContext,
): PeakAIRule[] => {
  const rules = getPeakAIRules();
  const isFemale = context.isMenstruatingFemale === true;

  const variables: Record<string, number | string | boolean> = {
    workloadFactor,
    peakScore,
    wellnessFinal,
    HLR: hlr,
    hlr,
    ...(context.cyclePhase !== undefined && { cyclePhase: context.cyclePhase }),
    ...(context.injured !== undefined && { injured: context.injured }),
    ...(context.canTrain !== undefined && { canTrain: context.canTrain }),
    ...(context.sickFactor !== undefined && { sickFactor: context.sickFactor }),
    ...(context.sleepRating !== undefined && { sleepRating: context.sleepRating }),
    ...(context.stressRating !== undefined && { stressRating: context.stressRating }),
  };

  const firing: PeakAIRule[] = [];

  for (const rule of rules) {
    const group = getRuleGroup(rule.alertId);

    // CP rules only fire for menstruating females
    if (group === "CP" && !isFemale) continue;

    // TE rules use pre-computed flags, not the clause parser
    if (group === "TE") {
      if (evaluateTERule(rule.alertId, context)) {
        firing.push(rule);
      }
      continue;
    }

    // All other rules use the clause parser
    if (evaluateRuleCondition(rule.condition, variables)) {
      firing.push(rule);
    }
  }

  console.log(`🎯 ${firing.length} rules fired:`, firing.map(r => `${r.alertId}(${r.alertTier})`).join(", "));
  return firing;
};

// Sort a list of rules within a colour tier by priority order
const sortByPriority = (
  rules: PeakAIRule[],
  priorityMap: Record<RuleGroup, number>,
): PeakAIRule[] => {
  return [...rules].sort((a, b) => {
    const pa = priorityMap[getRuleGroup(a.alertId)] ?? 99;
    const pb = priorityMap[getRuleGroup(b.alertId)] ?? 99;
    return pa - pb;
  });
};

// Apply the PEAK^ firing logic spec to select up to 3 alerts for display
export const selectAlertsToDisplay = (
  workloadFactor: number,
  peakScore: number,
  wellnessFinal: number,
  hlr: number,
  context: RuleContext,
): AlertSelection => {
  const firing = evaluateAllRules(workloadFactor, peakScore, wellnessFinal, hlr, context);

  const reds   = sortByPriority(firing.filter(r => r.alertTier === "Red"),   RED_PRIORITY);
  const ambers = sortByPriority(firing.filter(r => r.alertTier === "Amber"), AMBER_PRIORITY);
  const greens = sortByPriority(firing.filter(r => r.alertTier === "Green"), GREEN_PRIORITY);

  const topGreen = greens[0] ?? null;

  // Determine whether a Green may co-display
  const canShowGreen = (selectedNonGreen: PeakAIRule[]): boolean => {
    if (selectedNonGreen.length !== 1 || !topGreen) return false;
    const sole = selectedNonGreen[0];
    const group = getRuleGroup(sole.alertId);
    return group === "TE" || group === "T5";
  };

  // --- Apply display combination rules ---

  // 3 Red
  if (reds.length >= 3) {
    const alerts = reds.slice(0, 3);
    return { alerts, displayCombination: "3 Red", primaryTone: "Red" };
  }

  // 2 Red + 1 Amber
  if (reds.length === 2 && ambers.length >= 1) {
    const alerts = [...reds.slice(0, 2), ambers[0]];
    return { alerts, displayCombination: "2 Red + 1 Amber", primaryTone: "Red" };
  }

  // 2 Red (no amber)
  if (reds.length === 2) {
    const alerts = reds.slice(0, 2);
    return { alerts, displayCombination: "2 Red", primaryTone: "Red" };
  }

  // 1 Red + 2 Amber
  if (reds.length === 1 && ambers.length >= 2) {
    const alerts = [reds[0], ...ambers.slice(0, 2)];
    return { alerts, displayCombination: "1 Red + 2 Amber", primaryTone: "Red" };
  }

  // 1 Red + 1 Amber
  if (reds.length === 1 && ambers.length === 1) {
    const alerts = [reds[0], ambers[0]];
    return { alerts, displayCombination: "1 Red + 1 Amber", primaryTone: "Red" };
  }

  // 1 Red + 1 Green (only TE or T5 Red, no Amber)
  if (reds.length === 1 && ambers.length === 0) {
    const nonGreen = [reds[0]];
    if (canShowGreen(nonGreen)) {
      return { alerts: [reds[0], topGreen!], displayCombination: "1 Red + 1 Green", primaryTone: "Red" };
    }
    return { alerts: [reds[0]], displayCombination: "1 Red", primaryTone: "Red" };
  }

  // 2 Amber
  if (ambers.length >= 2) {
    const alerts = ambers.slice(0, 2);
    return { alerts, displayCombination: "2 Amber", primaryTone: "Amber" };
  }

  // 1 Amber + 1 Green (only TE or T5 Amber)
  if (ambers.length === 1) {
    const nonGreen = [ambers[0]];
    if (canShowGreen(nonGreen)) {
      return { alerts: [ambers[0], topGreen!], displayCombination: "1 Amber + 1 Green", primaryTone: "Amber" };
    }
    return { alerts: [ambers[0]], displayCombination: "1 Amber", primaryTone: "Amber" };
  }

  // 1 Green
  if (topGreen) {
    return { alerts: [topGreen], displayCombination: "1 Green", primaryTone: "Green" };
  }

  // Nothing fired
  return { alerts: [], displayCombination: "None", primaryTone: "Green" };
};

// Legacy single-rule helper — returns first alert from the selection (for backward compat)
export const getMatchingPeakAIRule = (
  workloadFactor: number,
  peakScore: number,
  wellnessFinal: number,
  hlr: number,
  context?: RuleContext,
): PeakAIRule | null => {
  const selection = selectAlertsToDisplay(workloadFactor, peakScore, wellnessFinal, hlr, context ?? {});
  return selection.alerts[0] ?? null;
};

// Simplified data interfaces for explainer functions
interface TrainingData {
  dateString: string;
  trainingLogs: TrainingLog[];
}

interface HealthData {
  dateString: string;
  healthLogs: HealthLog[];
}

interface WellnessData {
  dateString: string;
  wellnessLogs: WellnessLog[];
}

interface RecoveryData {
  dateString: string;
  recoveryLogs: RecoveryLog[];
}

interface NiggleData {
  dateString: string;
  niggleLogs: NiggleLog[];
  wellnessLogs: WellnessLog[];
}

interface SleepData {
  dateString: string;
  healthLogs: HealthLog[];
  wellnessLogs: WellnessLog[];
}

// Factor explainer functions
const workloadExplainer = (
  workloadFactor: number,
  trainingData: TrainingData[],
): string => {
  const getFactorCategory = (value: number): string => {
    if (value < 0.75)
      return "Sharp Load Spike / High Stress — Recent training is much higher than what your body has been prepared for. Fatigue accumulates quickly and injury risk is elevated if this is sustained.";

    if (value < 0.9)
      return "Elevated Load / High Challenge — Training demand is above your recent capacity. This zone can drive adaptation, but recovery quality becomes critical.";

    if (value < 1.0)
      return "Optimal Load / Building Phase — Recent training is slightly higher than your established capacity. This is an ideal zone for building fitness and progressive overload.";

    if (value < 1.1)
      return "Optimal Load / Maintenance or Sharpening — Recent training closely matches your established capacity. This supports consistency, performance stability, and readiness.";

    if (value <= 1.25)
      return "Reduced Load / Recovery or Taper — Training demand is lower than what you are conditioned for. Useful for deloads, recovery blocks, or pre-competition tapering.";

    return "Underloading / Deconditioning Risk — Training load is well below your capacity. If prolonged, fitness may decline and performance readiness can drop.";
  };

  // Calculate recent training metrics from simplified data
  const recentTrainingSessions = trainingData.reduce(
    (total, day) => total + day.trainingLogs.length,
    0,
  );
  const totalLoad = trainingData.reduce(
    (total, day) =>
      total +
      day.trainingLogs.reduce((dayTotal, log) => dayTotal + log.load, 0),
    0,
  );
  const averageLoad =
    recentTrainingSessions > 0
      ? (totalLoad / recentTrainingSessions).toFixed(1)
      : "N/A";

  const category = getFactorCategory(workloadFactor);

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Helper function to get day of the week
  const getDayOfWeek = (dateString: string): string => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    return date.toLocaleDateString("en-GB", { weekday: "long" });
  };

  // Helper function to calculate days ago
  const getDaysAgo = (dateString: string): number => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    const today = new Date();
    const diffTime = today.getTime() - date.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  // Generate day-by-day training breakdown
  const trainingBreakdown = trainingData
    .filter((day) => day.trainingLogs.length > 0) // Only include days with training
    .sort((a, b) => b.dateString.localeCompare(a.dateString)) // Sort by date (newest first)
    .map((day) => {
      const daysAgo = getDaysAgo(day.dateString);
      const formattedDate = formatDateForDisplay(day.dateString);
      const dayOfWeek = getDayOfWeek(day.dateString);
      const sessionCount = day.trainingLogs.length;
      const dailyTotalLoad = day.trainingLogs.reduce(
        (total, log) => total + log.load,
        0,
      );

      const sessionLabel =
        sessionCount === 1 ? "1 session" : `${sessionCount} sessions`;
      const daysAgoLabel =
        daysAgo === 0
          ? "today"
          : daysAgo === 1
            ? "1 day ago"
            : `${daysAgo} days ago`;

      let dayBreakdown = `### ${dayOfWeek}: ${sessionLabel} ${daysAgoLabel} on ${formattedDate}\n\nTotal daily workload: ${dailyTotalLoad}\n\n`;

      // Add each session
      day.trainingLogs.forEach((log, index) => {
        dayBreakdown += `#### Session ${index + 1}\n\n`;
        dayBreakdown += `Workload: ${log.load}\n\n`;
        if (log.notes && log.notes.trim()) {
          dayBreakdown += `Notes: ${log.notes}\n\n`;
        }
        if (log.tag && log.tag.length > 0) {
          dayBreakdown += `Tags: (${log.tag.join(", ")})\n\n`;
        }
      });

      return dayBreakdown;
    })
    .join("---\n\n");

  const summary = `${category} Over the past 7 days, the athlete completed ${recentTrainingSessions} training sessions with an average load of ${averageLoad}`;

  return trainingBreakdown
    ? `${summary}\n\n The following is a breakdown of the athlete's training over the past 7 days: \n\n${trainingBreakdown}`
    : summary;
};

const HLRExplainer = (
  hlr: number,
  healthDataInput: HealthData[],
  excludeGarmin = false,
): string => {
  const garminLogs = healthDataInput
    .flatMap((day) => day.healthLogs)
    .filter(isGarminImportedHealthLog);
  const garminMetricsWithheld =
    excludeGarmin && garminLogs.some(healthLogHasCardioMetrics);
  const healthData = excludeGarmin
    ? healthDataInput.map((day) => ({
        ...day,
        healthLogs: day.healthLogs.filter((log) => !isGarminImportedHealthLog(log)),
      }))
    : healthDataInput;
  const visibleMetricLogs = healthData
    .flatMap((day) => day.healthLogs)
    .filter(healthLogHasCardioMetrics);

  // Get today's data
  const today = new Date();
  const todayDateID = generateDateID(today);
  const todayHealthData = healthData.find(
    (day) => day.dateString === todayDateID,
  );
  const todayLog = todayHealthData?.healthLogs[0]; // Assuming one health log per day

  // Calculate recent health metrics from simplified data
  const recentHealthLogs = healthData.flatMap((day) => day.healthLogs);
  const validHRVLogs = recentHealthLogs.filter((log) => log.hrv && log.hrv > 0);
  const validRHRLogs = recentHealthLogs.filter((log) => log.rhr && log.rhr > 0);
  const validRespLogs = recentHealthLogs.filter(
    (log) => log.respRate && log.respRate > 0,
  );

  const avgHRV =
    validHRVLogs.length > 0
      ? (
          validHRVLogs.reduce((sum, log) => sum + log.hrv!, 0) /
          validHRVLogs.length
        ).toFixed(1)
      : "N/A";
  const avgRHR =
    validRHRLogs.length > 0
      ? (
          validRHRLogs.reduce((sum, log) => sum + log.rhr!, 0) /
          validRHRLogs.length
        ).toFixed(1)
      : "N/A";
  const avgResp =
    validRespLogs.length > 0
      ? (
          validRespLogs.reduce((sum, log) => sum + log.respRate!, 0) /
          validRespLogs.length
        ).toFixed(1)
      : "N/A";

  // Get today's values
  const todayHRV = todayLog?.hrv ? todayLog.hrv.toFixed(1) : "N/A";
  const todayRHR = todayLog?.rhr ? todayLog.rhr.toFixed(1) : "N/A";
  const todayResp = todayLog?.respRate ? todayLog.respRate.toFixed(1) : "N/A";

  const baseExplanation = (() => {
    if (hlr > 1.07)
      return `Strong — physiological recovery signals are well above baseline, consistent with positive adaptation across heart, lung, and autonomic systems`;

    if (hlr >= 1.03 && hlr <= 1.07)
      return `Good — physiological recovery is above baseline, with cardiorespiratory markers reliably tracking above average`;

    if (hlr >= 1.0 && hlr < 1.03)
      return `On track — physiological recovery is stable, with heart and lung markers at or slightly above baseline`;

    if (hlr >= 0.97 && hlr < 1.0)
      return `Steady — physiological recovery is within normal day-to-day variation, reflecting a balanced recovery state`;

    if (hlr >= 0.93 && hlr < 0.97)
      return `Reduced — early physiological signs of reduced recovery present, with subtle downward shifts in cardiorespiratory markers`;

    return `Low — physiological recovery is meaningfully below baseline, indicating under-recovery. This pattern may also be associated with an increased risk of illness`;
  })();

  // Build detailed health breakdown
  const healthBreakdown = [
    `**Today's Health Data**: Heart Rate Variability ${todayHRV}ms, Resting Heart Rate ${todayRHR}bpm, Respiratory Rate ${todayResp}bpm`,
    `**7-Day Averages**: Heart Rate Variability ${avgHRV}ms, Resting Heart Rate ${avgRHR}bpm, Respiratory Rate ${avgResp}bpm`,
  ].join("\n\n");

  // HLR is Peak's own derivation and stays in the report. Only the raw
  // Garmin readings underneath it are withheld.
  if (garminMetricsWithheld && visibleMetricLogs.length === 0) {
    return `${baseExplanation}.\n\n${GARMIN_AI_EXCLUSION_NOTE}`;
  }

  if (garminMetricsWithheld) {
    return `${baseExplanation}.\n\n${GARMIN_AI_EXCLUSION_NOTE}\n\nNon-Garmin health data still on record:\n\n${healthBreakdown}`;
  }

  return `${baseExplanation}.\n\n${healthBreakdown}`;
};

const sickExplainer = (
  sickFactor: number,
  wellnessData: WellnessData[],
  todayDateID: string,
): string => {
  if (sickFactor >= 1.0) return "";

  // sickFactor is a multi-day recovery-decay value: it stays below 1.0 for
  // several days after the athlete's most recent sick entry, so a present-tense
  // "is sick" reads as wrong once the actual sick day was days ago. List out
  // every day the box was actually checked, with how long ago each one was,
  // so the AI knows exactly when illness was reported instead of just a
  // decayed score (see issue #185).
  //
  // Diff against todayDateID (the athlete's local calendar day), not the
  // server's wall-clock `new Date()` — Cloud Functions run in UTC while
  // dateIDs are the athlete's local day, so diffing against "now" would be
  // off by a day around midnight depending on the athlete's timezone.
  const parseDateID = (dateString: string): Date =>
    new Date(
      `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}T00:00:00Z`,
    );
  const getDaysAgo = (dateString: string): number => {
    const diffTime =
      parseDateID(todayDateID).getTime() - parseDateID(dateString).getTime();
    return Math.round(diffTime / (1000 * 60 * 60 * 24));
  };

  const sickDates = wellnessData
    .filter((day) => day.wellnessLogs.some((log) => log.sick))
    .map((day) => day.dateString)
    .sort((a, b) => b.localeCompare(a)); // newest first
  const sickDays = sickDates.length;

  // A single sick-day entry already decays sickFactor to ~0.7-0.8
  // (calculatePeakScoreVersion2.ts), so a 0.9 cutoff called every checked
  // box "very sick". Reserve that wording for a lower factor, which only
  // happens with multiple consecutive sick days.
  const severity = sickFactor < 0.6 ? "very sick" : "sick";

  if (sickDays === 0) {
    // sickFactor is still decayed from a sick entry outside the 7-day
    // window this function was given — no specific date to report.
    return `the athlete reported being ${severity} recently, though not within the last 7 days`;
  }

  const daysAgoLabels = sickDates.map((date) => {
    const daysAgo = getDaysAgo(date);
    if (daysAgo <= 0) return "today";
    if (daysAgo === 1) return "1 day ago";
    return `${daysAgo} days ago`;
  });
  const daysAgoText =
    daysAgoLabels.length === 1
      ? daysAgoLabels[0]
      : `${daysAgoLabels.slice(0, -1).join(", ")} and ${daysAgoLabels[daysAgoLabels.length - 1]}`;

  return `the athlete reported being ${severity} ${daysAgoText} (${sickDays} of the past 7 days)`;
};

const recoveryExplainer = (recoveryData: RecoveryData[]): string => {
  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Helper function to get day of the week
  const getDayOfWeek = (dateString: string): string => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    return date.toLocaleDateString("en-GB", { weekday: "long" });
  };

  // Helper function to calculate days ago
  const getDaysAgo = (dateString: string): number => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    const today = new Date();
    const diffTime = today.getTime() - date.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  // Count total recovery activities
  const totalRecoveryActivities = recoveryData.reduce(
    (total, day) => total + day.recoveryLogs.length,
    0,
  );

  // Generate day-by-day recovery breakdown
  const recoveryBreakdown = recoveryData
    .filter((day) => day.recoveryLogs.length > 0) // Only include days with recovery activities
    .sort((a, b) => b.dateString.localeCompare(a.dateString)) // Sort by date (newest first)
    .map((day) => {
      const daysAgo = getDaysAgo(day.dateString);
      const formattedDate = formatDateForDisplay(day.dateString);
      const dayOfWeek = getDayOfWeek(day.dateString);
      const activityCount = day.recoveryLogs.length;

      const activityLabel =
        activityCount === 1
          ? "1 recovery activity"
          : `${activityCount} recovery activities`;
      const daysAgoLabel =
        daysAgo === 0
          ? "today"
          : daysAgo === 1
            ? "1 day ago"
            : `${daysAgo} days ago`;

      let dayBreakdown = `### ${dayOfWeek}: ${activityLabel} ${daysAgoLabel} on ${formattedDate}\n\n`;

      // Add each recovery activity
      day.recoveryLogs.forEach((log, index) => {
        dayBreakdown += `#### Recovery Activity ${index + 1}\n\n`;
        if (log.selectedRecovery && log.selectedRecovery.length > 0) {
          dayBreakdown += `Activities: ${log.selectedRecovery.join(", ")}\n\n`;
        }
        if (log.notes && log.notes.trim()) {
          dayBreakdown += `Notes: ${log.notes}\n\n`;
        }
      });

      return dayBreakdown;
    })
    .join("");

  const summary =
    totalRecoveryActivities > 0
      ? `The athlete completed ${totalRecoveryActivities} recovery activities over the past 7 days`
      : "No recovery activities recorded over the past 7 days";

  return recoveryBreakdown ? `${summary}\n\n${recoveryBreakdown}` : summary;
};

const sleepExplainer = (
  sleepData: SleepData[],
  lastNightOnly = false,
  sleepGoalHours?: number,
  excludeGarmin = false,
): string => {
  const annotatedSleepData = sleepData.map((day) => {
    const garminSleepWithheld =
      excludeGarmin &&
      day.healthLogs.some(
        (log) => isGarminImportedHealthLog(log) && healthLogHasSleep(log),
      );
    return {
      ...day,
      healthLogs: excludeGarmin
        ? day.healthLogs.filter((log) => !isGarminImportedHealthLog(log))
        : day.healthLogs,
      garminSleepWithheld,
    };
  });
  const anyGarminSleepWithheld = annotatedSleepData.some(
    (day) => day.garminSleepWithheld,
  );
  const sortedSleepData = [...annotatedSleepData].sort((a, b) =>
    b.dateString.localeCompare(a.dateString),
  );

  if (lastNightOnly) {
    const lastNightWithData =
      sortedSleepData.find(
        (day) =>
          calcSleepWithWellnessFallback(day.healthLogs, day.wellnessLogs) > 0,
      ) || sortedSleepData[0];

    if (!lastNightWithData) {
      return anyGarminSleepWithheld
        ? GARMIN_AI_EXCLUSION_NOTE
        : "Last night's sleep: no sleep data available.";
    }

    const sleepDurationSeconds = calcSleepWithWellnessFallback(
      lastNightWithData.healthLogs,
      lastNightWithData.wellnessLogs,
    );
    if (lastNightWithData.garminSleepWithheld && sleepDurationSeconds === 0) {
      return GARMIN_AI_EXCLUSION_NOTE;
    }
    const formattedDuration = formatSleepDuration(sleepDurationSeconds);
    const hasHealthSleep = lastNightWithData.healthLogs.some(
      (log) =>
        (log.sleepTimeline && log.sleepTimeline.length > 0) ||
        (log.sleepDuration && log.sleepDuration > 0),
    );
    const source = hasHealthSleep ? "wearable data" : "wellness log";
    const line = `Last night's sleep: *${formattedDuration}* (source: ${source}).`;

    return lastNightWithData.garminSleepWithheld
      ? `${GARMIN_AI_EXCLUSION_NOTE}\n\n${line}`
      : line;
  }

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Helper function to get day of the week
  const getDayOfWeek = (dateString: string): string => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    return date.toLocaleDateString("en-GB", { weekday: "long" });
  };

  // Helper function to calculate days ago
  const getDaysAgo = (dateString: string): number => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    const today = new Date();
    const diffTime = today.getTime() - date.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  // Calculate sleep duration for each day and create breakdown
  const sleepBreakdown = sortedSleepData
    .map((day) => {
      const daysAgo = getDaysAgo(day.dateString);
      const formattedDate = formatDateForDisplay(day.dateString);
      const dayOfWeek = getDayOfWeek(day.dateString);

      // Calculate sleep duration using the existing utility function
      const sleepDurationSeconds = calcSleepWithWellnessFallback(
        day.healthLogs,
        day.wellnessLogs,
      );
      const formattedDuration = formatSleepDuration(sleepDurationSeconds);

      // Sleep in this app is stored under the day the athlete WAKES UP.
      // So daysAgo=0 (today's post) = last night's sleep.
      // daysAgo=1 (yesterday's post) = the night before last. Etc.
      const nightLabel =
        daysAgo === 0
          ? "last night"
          : daysAgo === 1
            ? "the night before last"
            : `${daysAgo + 1} nights ago`;

      // Determine sleep source (health data vs wellness fallback)
      const hasHealthSleep = day.healthLogs.some(
        (log) =>
          (log.sleepTimeline && log.sleepTimeline.length > 0) ||
          (log.sleepDuration && log.sleepDuration > 0),
      );
      const source =
        day.garminSleepWithheld && sleepDurationSeconds === 0
          ? "Garmin data excluded"
          : hasHealthSleep
            ? "wearable data"
            : "wellness log";

      // Use descriptive label instead of specific duration — AI must not quote hours/minutes
      const durationLabel =
        day.garminSleepWithheld && sleepDurationSeconds === 0
        ? "unavailable — we do not have access to this data due to Garmin's policies"
        : sleepDurationSeconds === 0 ? "no data recorded"
        : sleepDurationSeconds < 5.5 * 3600 ? "very short night"
        : sleepDurationSeconds < 6.5 * 3600 ? "short night — below target"
        : sleepDurationSeconds < 7.5 * 3600 ? "moderate — approaching target"
        : sleepDurationSeconds < 8.5 * 3600 ? "good — on target"
        : "excellent — above target";

      return `### ${dayOfWeek} — ${nightLabel} (${formattedDate}): ${durationLabel} | Source: ${source}\n\n`;
    })
    .join("");

  // --- Today's sleep snapshot ---
  // Sleep is logged under the day the athlete wakes up. sortedSleepData[0] = today's post = last night's sleep.
  // If today has no data yet (wearable not synced), fall back to most recent with data.
  const todaySleepData = sortedSleepData[0];
  const todaySleepSeconds = todaySleepData
    ? calcSleepWithWellnessFallback(todaySleepData.healthLogs, todaySleepData.wellnessLogs)
    : 0;
  const todaySleepHours = todaySleepSeconds / 3600;
  const todaySleepFormatted = formatSleepDuration(todaySleepSeconds);
  const todayWellnessLog = todaySleepData?.wellnessLogs?.[0];
  const todaySleepQuality = todayWellnessLog?.sleep ?? null; // 1–7 scale

  const sleepQualityLabel = (q: number): string => {
    if (q <= 1) return "awful";
    if (q <= 2) return "poor";
    if (q <= 3) return "low";
    if (q <= 4) return "fair";
    if (q <= 5) return "good";
    if (q <= 6) return "great";
    return "excellent";
  };

  // Determine if today's sleep has room for improvement
  // Thresholds: duration < 7 hours OR quality < 5 out of 7
  const todayDurationNeedsImprovement = todaySleepHours > 0 && todaySleepHours < 7;
  const todayQualityNeedsImprovement = todaySleepQuality !== null && todaySleepQuality < 5;
  const todayNeedsImprovement = todayDurationNeedsImprovement || todayQualityNeedsImprovement;

  // Build today's snapshot section
  // "Today's post" = waking up today = last night's sleep
  const todaySnapshotLines: string[] = [];
  if (todaySleepData?.garminSleepWithheld && todaySleepSeconds === 0) {
    todaySnapshotLines.push(`**Last night's duration**: ${GARMIN_AI_EXCLUSION_NOTE}`);
  } else if (todaySleepSeconds > 0) {
    todaySnapshotLines.push(`**Last night's duration**: ${todaySleepFormatted}`);
  } else {
    todaySnapshotLines.push(`**Last night's duration**: Not yet synced — use wellness sleep quality below`);
  }
  if (todaySleepQuality !== null) {
    todaySnapshotLines.push(
      `**Sleep Quality**: ${sleepQualityLabel(todaySleepQuality)} (${todaySleepQuality}/7)`,
    );
  }
  if (todayDurationNeedsImprovement) {
    const shortfallSeconds = 7 * 3600 - todaySleepSeconds;
    todaySnapshotLines.push(
      `⚠️ Duration is below the 7-hour threshold by ${formatSleepDuration(shortfallSeconds)}`,
    );
  }
  if (todayQualityNeedsImprovement) {
    todaySnapshotLines.push(
      `⚠️ Sleep quality is below a healthy threshold (target ≥5/7)`,
    );
  }

  // --- 7-day average & improvement gap (only shown when today needs improvement) ---
  const daysWithSleepData = sleepData.filter(
    (day) => calcSleepWithWellnessFallback(day.healthLogs, day.wellnessLogs) > 0,
  );
  const daysWithSleep = daysWithSleepData.length;

  const totalSleepSeconds = sleepData.reduce(
    (total, day) => total + calcSleepWithWellnessFallback(day.healthLogs, day.wellnessLogs),
    0,
  );
  const averageSleepSeconds = daysWithSleep > 0 ? totalSleepSeconds / daysWithSleep : 0;
  const avgSleepHours = averageSleepSeconds / 3600;
  const formattedAverageSleep = formatSleepDuration(averageSleepSeconds);

  // Average quality over 7 days
  const qualityLogs = sleepData.flatMap((d) => d.wellnessLogs).filter((l) => l.sleep != null);
  const avgQuality = qualityLogs.length > 0
    ? qualityLogs.reduce((s, l) => s + (l.sleep ?? 0), 0) / qualityLogs.length
    : null;

  // Use athlete's personal sleep goal; fall back to 8h if not set
  const targetHours = sleepGoalHours && sleepGoalHours > 0 ? sleepGoalHours : 8;
  const targetSeconds = targetHours * 3600;
  const hasPersonalGoal = !!(sleepGoalHours && sleepGoalHours > 0);

  // Directional comparisons only — no specific times exposed
  const lastNightVsAvg = averageSleepSeconds > 0 && todaySleepSeconds > 0
    ? todaySleepSeconds >= averageSleepSeconds ? "above their weekly average" : "below their weekly average"
    : null;
  const lastNightVsGoal = todaySleepSeconds > 0
    ? todaySleepSeconds >= targetSeconds
      ? `meeting their${hasPersonalGoal ? " personal" : ""} sleep goal`
      : `short of their${hasPersonalGoal ? " personal" : ""} sleep goal`
    : null;
  const avgVsGoal = averageSleepSeconds > 0
    ? averageSleepSeconds >= targetSeconds
      ? `weekly average is meeting the${hasPersonalGoal ? " personal" : ""} sleep goal`
      : `weekly average is below the${hasPersonalGoal ? " personal" : ""} sleep goal`
    : null;
  const qualityVsAvg = avgQuality !== null && todaySleepQuality !== null
    ? todaySleepQuality >= avgQuality ? "at or above their weekly average" : "below their weekly average"
    : null;

  // Build output
  let result = `### Last Night's Sleep (today's post)\n${todaySnapshotLines.join("\n")}\n\n`;

  if (todayNeedsImprovement) {
    result += `### Last 7 Days — Sleep Trend\n`;
    result += `Days with sleep data: ${daysWithSleep} of 7\n`;
    if (lastNightVsAvg) result += `Last night: ${lastNightVsAvg}.\n`;
    if (lastNightVsGoal) result += `Last night: ${lastNightVsGoal}.\n`;
    if (avgVsGoal) result += `Trend: ${avgVsGoal}.\n`;
    if (qualityVsAvg) result += `Sleep quality last night: ${qualityVsAvg}.\n`;
    result += `\n${sleepBreakdown}`;

    result += `### What needs to improve\n`;
    if (todayDurationNeedsImprovement) {
      result += `- Sleep volume: last night fell short of the athlete's sleep goal. Improve sleep volume.\n`;
    }
    if (todayQualityNeedsImprovement) {
      result += `- Sleep quality: last night was below a restorative threshold. Improve sleep quality.\n`;
    }
  } else {
    result += `### Last 7 Days — Sleep Summary\n`;
    result += `Days with sleep data: ${daysWithSleep} of 7`;
    if (lastNightVsAvg) result += ` | Last night: ${lastNightVsAvg}`;
    if (lastNightVsGoal) result += `, ${lastNightVsGoal}`;
    if (avgQuality !== null) result += ` | Quality: ${sleepQualityLabel(avgQuality)}`;
    result += "\n";
  }

  if (anyGarminSleepWithheld) {
    result = `${GARMIN_AI_EXCLUSION_NOTE}\n\n${result}`;
  }

  return result;
};

const wellnessExplainer = (
  wellnessScore: number,
  wellnessCondition: string,
  peakRange: number,
  wellnessRange: number,
  wellnessData: WellnessData[],
): string => {
  // Helper function to convert 1-7 scale to descriptive labels
  const getWellnessLabel = (value: number): string => {
    if (value <= 1) return "awful";
    if (value <= 2) return "poor";
    if (value <= 3) return "low";
    if (value <= 4) return "fair";
    if (value <= 5) return "good";
    if (value <= 6) return "great";
    return "excellent";
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Helper function to get day of the week
  const getDayOfWeek = (dateString: string): string => {
    const date = new Date(
      dateString.slice(0, 4) +
        "-" +
        dateString.slice(4, 6) +
        "-" +
        dateString.slice(6, 8),
    );
    return date.toLocaleDateString("en-GB", { weekday: "long" });
  };

  // Get today's wellness data
  const today = new Date();
  const todayDateID = generateDateID(today);
  const todayWellness = wellnessData.find(
    (day) => day.dateString === todayDateID,
  );
  const todayLog = todayWellness?.wellnessLogs[0]; // Assuming one wellness log per day

  // Calculate 7-day averages
  const recentWellnessLogs = wellnessData.flatMap((day) => day.wellnessLogs);
  const avgWellnessScore =
    recentWellnessLogs.length > 0
      ? (
          recentWellnessLogs.reduce((sum, log) => sum + log.wellnessScore, 0) /
          recentWellnessLogs.length
        ).toFixed(1)
      : "N/A";

  // Calculate individual metric averages
  const metricKeys = [
    "sleep",
    "stress",
    "mood",
    "health",
    "physical",
    "recovery",
  ] as const;
  const metricLabels: Record<string, string> = {
    sleep: "Sleep Quality",
    stress: "Stress Management",
    mood: "Mood",
    health: "Health",
    physical: "Physical Readiness",
    recovery: "Recovery",
  };

  const metricAverages: Record<string, number> = {};
  for (const key of metricKeys) {
    metricAverages[key] =
      recentWellnessLogs.length > 0
        ? recentWellnessLogs.reduce((sum, log) => sum + log[key], 0) /
          recentWellnessLogs.length
        : 0;
  }

  const baseExplanation = (() => {
    if (peakRange < wellnessRange) {
      return `The athlete's wellness is ${wellnessCondition} BUT the peak score is brought down by either HLR or increasing workload.`;
    } else if (peakRange === wellnessRange) {
      return `The athlete's wellness today is ${wellnessCondition}`;
    } else {
      return `Although the athlete's wellness is ${wellnessCondition}, the peak score is stronger due to lower training load recently`;
    }
  })();

  // Build today's wellness snapshot
  const todaySnapshot = todayLog
    ? [
        `**Sleep Quality**: ${getWellnessLabel(todayLog.sleep)} (${todayLog.sleep}/7)`,
        `**Stress Management**: ${getWellnessLabel(todayLog.stress)} (${todayLog.stress}/7)`,
        `**Mood**: ${getWellnessLabel(todayLog.mood)} (${todayLog.mood}/7)`,
        `**Health**: ${getWellnessLabel(todayLog.health)} (${todayLog.health}/7)`,
        `**Physical Readiness**: ${getWellnessLabel(todayLog.physical)} (${todayLog.physical}/7)`,
        `**Recovery**: ${getWellnessLabel(todayLog.recovery)} (${todayLog.recovery}/7)`,
        `**Overall Wellness Score**: ${todayLog.wellnessScore}`,
      ].join("\n")
    : `No wellness data available for today.`;

  // Build day-by-day wellness breakdown for the last 7 days
  const dayByDayBreakdown = wellnessData
    .filter((day) => day.wellnessLogs.length > 0)
    .sort((a, b) => b.dateString.localeCompare(a.dateString)) // newest first
    .map((day) => {
      const log = day.wellnessLogs[0];
      const dayOfWeek = getDayOfWeek(day.dateString);
      const formattedDate = formatDateForDisplay(day.dateString);
      const isToday = day.dateString === todayDateID;

      let dayEntry = `### ${dayOfWeek}${isToday ? " (Today)" : ""} — ${formattedDate}\n`;
      dayEntry += `Overall: ${getWellnessLabel(log.wellnessScore)} (${log.wellnessScore}) | `;
      dayEntry += `Sleep: ${log.sleep}/7 | Stress: ${log.stress}/7 | Mood: ${log.mood}/7 | Health: ${log.health}/7 | Physical: ${log.physical}/7 | Recovery: ${log.recovery}/7`;

      // Flag any metric that dropped to 3 or below
      const lowMetrics = metricKeys.filter((key) => log[key] <= 3);
      if (lowMetrics.length > 0) {
        dayEntry += `\n⚠️ Low: ${lowMetrics.map((key) => `${metricLabels[key]} (${getWellnessLabel(log[key])})`).join(", ")}`;
      }

      return dayEntry;
    })
    .join("\n\n");

  // 7-day averages summary
  const averagesSummary = metricKeys
    .map(
      (key) =>
        `**${metricLabels[key]}**: ${getWellnessLabel(metricAverages[key])} (${metricAverages[key].toFixed(1)}/7)`,
    )
    .join("\n");

  // Rank all metrics by average (lowest first) to identify struggling areas
  const sortedMetrics = metricKeys
    .map((key) => ({
      name: metricLabels[key],
      key,
      average: metricAverages[key],
    }))
    .sort((a, b) => a.average - b.average);

  const worstMetric = sortedMetrics[0];
  const secondWorstMetric = sortedMetrics[1];
  const bestMetric = sortedMetrics[sortedMetrics.length - 1];

  // Detect trends: compare first half of week vs second half
  const sortedDays = wellnessData
    .filter((day) => day.wellnessLogs.length > 0)
    .sort((a, b) => a.dateString.localeCompare(b.dateString));

  let trendAnalysis = "";
  if (sortedDays.length >= 4) {
    const midpoint = Math.floor(sortedDays.length / 2);
    const firstHalf = sortedDays.slice(0, midpoint);
    const secondHalf = sortedDays.slice(midpoint);

    const firstHalfAvg =
      firstHalf
        .flatMap((d) => d.wellnessLogs)
        .reduce((sum, log) => sum + log.wellnessScore, 0) /
      firstHalf.flatMap((d) => d.wellnessLogs).length;
    const secondHalfAvg =
      secondHalf
        .flatMap((d) => d.wellnessLogs)
        .reduce((sum, log) => sum + log.wellnessScore, 0) /
      secondHalf.flatMap((d) => d.wellnessLogs).length;

    const diff = secondHalfAvg - firstHalfAvg;
    if (diff > 3) {
      trendAnalysis = `\n\n**Trend**: Wellness is trending upward — the athlete's scores improved significantly in the second half of the week (avg ${secondHalfAvg.toFixed(1)}) compared to the first half (avg ${firstHalfAvg.toFixed(1)}).`;
    } else if (diff < -3) {
      trendAnalysis = `\n\n**Trend**: Wellness is trending downward — the athlete's scores declined in the second half of the week (avg ${secondHalfAvg.toFixed(1)}) compared to the first half (avg ${firstHalfAvg.toFixed(1)}). Investigate what changed.`;
    } else {
      trendAnalysis = `\n\n**Trend**: Wellness has been relatively stable across the week (first half avg ${firstHalfAvg.toFixed(1)}, second half avg ${secondHalfAvg.toFixed(1)}).`;
    }

    // Per-metric trend for the worst metric
    const worstKey = worstMetric.key;
    const firstHalfWorst =
      firstHalf
        .flatMap((d) => d.wellnessLogs)
        .reduce((sum, log) => sum + log[worstKey], 0) /
      firstHalf.flatMap((d) => d.wellnessLogs).length;
    const secondHalfWorst =
      secondHalf
        .flatMap((d) => d.wellnessLogs)
        .reduce((sum, log) => sum + log[worstKey], 0) /
      secondHalf.flatMap((d) => d.wellnessLogs).length;

    const worstDiff = secondHalfWorst - firstHalfWorst;
    if (Math.abs(worstDiff) > 0.5) {
      trendAnalysis += ` ${worstMetric.name} specifically has ${worstDiff > 0 ? "improved" : "declined"} from ${getWellnessLabel(firstHalfWorst)} to ${getWellnessLabel(secondHalfWorst)} across the week.`;
    }
  }

  const strugglingArea =
    recentWellnessLogs.length > 0
      ? `\n\n**Biggest area to improve**: ${worstMetric.name} has been the weakest metric this week at ${getWellnessLabel(worstMetric.average)} (${worstMetric.average.toFixed(1)}/7), followed by ${secondWorstMetric.name} at ${getWellnessLabel(secondWorstMetric.average)} (${secondWorstMetric.average.toFixed(1)}/7). The strongest metric is ${bestMetric.name} at ${getWellnessLabel(bestMetric.average)} (${bestMetric.average.toFixed(1)}/7).`
      : "";

  let result = `${baseExplanation}.\n\n`;
  result += `### Today's Wellness\n${todaySnapshot}\n\n`;
  result += `### 7-Day Averages\n${averagesSummary}\nAverage Overall Wellness Score: ${avgWellnessScore}\n\n`;
  result += `### Day-by-Day Wellness Breakdown\n${dayByDayBreakdown || "No wellness data logged this week."}`;
  result += strugglingArea;
  result += trendAnalysis;
  result += `\n\n*Note: Wellness metrics are scored 1-7 where: 1=awful, 2=poor, 3=low, 4=fair, 5=good, 6=great, 7=excellent*`;

  return result;
};

// Helper to format a dateID (YYYYMMDD) into a readable date string
const formatDateIDForDisplay = (dateID: string): string => {
  const date = new Date(
    dateID.slice(0, 4) + "-" + dateID.slice(4, 6) + "-" + dateID.slice(6, 8),
  );
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Build the "Health Issues (Niggles / Injury / Sickness) — Last 7 Days" section
const healthIssuesExplainer = (niggleData: NiggleData[]): string => {
  const niggleLines: string[] = [];
  const injuryLines: string[] = [];
  const sicknessLines: string[] = [];

  for (const day of niggleData) {
    const label = formatDateIDForDisplay(day.dateString);

    // Niggles
    for (const niggle of day.niggleLogs) {
      const parts: string[] = [];
      if (niggle.notes) parts.push(niggle.notes);
      parts.push(`pain ${niggle.pain}/10`, `impact ${niggle.impact}/10`);
      niggleLines.push(`- **${label}**: ${parts.join(" · ")}`);
    }

    // Injury / sickness flags from wellness log
    for (const w of day.wellnessLogs) {
      if (w.injured) {
        const capacity = w.trainingCapacity ?? "unknown";
        injuryLines.push(
          `- **${label}**: Athlete marked as injured — training capacity: ${capacity}`,
        );
      }
      if (w.sick) {
        sicknessLines.push(`- **${label}**: Athlete reported feeling sick`);
      }
    }
  }

  if (
    niggleLines.length === 0 &&
    injuryLines.length === 0 &&
    sicknessLines.length === 0
  ) {
    return `## Health Issues (Last 7 Days)\n\nNo niggles, injuries, or sickness reported in the last 7 days.`;
  }

  let section = `## Health Issues (Last 7 Days)\n\n`;

  if (niggleLines.length > 0) {
    section += `**Niggles**\n\n${niggleLines.join("\n")}\n\n`;
  }
  if (injuryLines.length > 0) {
    section += `**Injury**\n\n${injuryLines.join("\n")}\n\n`;
  }
  if (sicknessLines.length > 0) {
    section += `**Sickness**\n\n${sicknessLines.join("\n")}\n\n`;
  }

  return section.trim();
};

// Build the "Last 7 Days AI Insights" markdown section
const buildAIInsightsSection = (
  recentAIInsights: Record<string, string>,
): string => {
  const entries = Object.entries(recentAIInsights).sort((a, b) =>
    b[0].localeCompare(a[0]),
  ); // newest first

  if (entries.length === 0) return "";

  const lines = entries
    .map(
      ([dateID, insight]) =>
        `### ${formatDateIDForDisplay(dateID)}\n\n${insight}`,
    )
    .join("\n\n");

  return `## Last 7 Days AI Insights\n\n${lines}`;
};

// Main function to generate athlete report from 7 days of raw data
export const generateAthleteReport = (
  recent7Days: DayPost[],
  simple = false,
  recentAIInsights?: Record<string, string>,
  cyclePhase?: string | null,
  isFemale?: boolean,
  sleepGoalHours?: number,
  excludeGarmin = false,
): string | null => {
  const dayPosts = recent7Days;
  const today = new Date();
  const todayDateID = generateDateID(today);

  // Find today's peak data
  const todayPost = dayPosts.find((post) => post.dateID === todayDateID);
  const todayPeak = todayPost?.peakScoreLog;

  if (!todayPeak) {
    return null;
  }

  const peakScore = todayPeak.peakScore;
  const workloadFactor = todayPeak.workloadFactor || 1.0;
  const wellnessFinal = todayPeak.wellnessFinal || 0;
  const sickFactor = todayPeak.sickFactor || 1.0;
  const hlr = todayPeak.hlr || 1.0;
  const title = "Peak Score Analysis";
  const roundedScore = Math.round(peakScore);

  // --- Compute TE (Trend & Event) flags from dayPosts ---
  // Sort dayPosts newest-first for trend calculations
  const sorted = [...dayPosts].sort((a, b) => b.dateID.localeCompare(a.dateID));

  // Today's wellness log
  const todayWellness = todayPost?.wellnessLogs?.[0];

  // Helper: get wellness value for N days ago (0 = today)
  const getWellness = (daysAgo: number) =>
    sorted[daysAgo]?.wellnessLogs?.[0];

  // Helper: get HLR for N days ago
  const getHLR = (daysAgo: number) =>
    sorted[daysAgo]?.peakScoreLog?.hlr ?? null;

  // Helper: get peak score for N days ago
  const getPeakScore = (daysAgo: number) =>
    sorted[daysAgo]?.peakScoreLog?.peakScore ?? null;

  // TE-03: Days since last session
  let daysSinceLastSession = 0;
  for (let i = 1; i < sorted.length; i++) {
    if ((sorted[i]?.trainingLogs?.length ?? 0) > 0) break;
    daysSinceLastSession = i;
  }

  // TE-01 & TE-02: Load calculations from 28-day data
  const allLoads28: number[] = dayPosts
    .flatMap((p) => p.trainingLogs?.map((t) => t.load ?? 0) ?? [])
    .filter((l) => l > 0);
  const avg28Load = allLoads28.length > 0
    ? allLoads28.reduce((a, b) => a + b, 0) / allLoads28.length
    : 0;
  const max28Load = allLoads28.length > 0 ? Math.max(...allLoads28) : 0;

  // TE-01: Two consecutive days where session load > 2x 28-day avg
  const day0Loads = sorted[0]?.trainingLogs?.map((t) => t.load ?? 0).filter((l) => l > 0) ?? [];
  const day1Loads = sorted[1]?.trainingLogs?.map((t) => t.load ?? 0).filter((l) => l > 0) ?? [];
  const consecutiveExtremeLoadDays =
    avg28Load > 0 &&
    day0Loads.some((l) => l > 2 * avg28Load) &&
    day1Loads.some((l) => l > 2 * avg28Load);

  // TE-02: Yesterday's load > 2x highest recorded load of last 28 days
  const extremeLoadSpikeYesterday =
    max28Load > 0 && day1Loads.some((l) => l > 2 * max28Load);

  // TE-04: Soreness > 4 for 3 consecutive days
  const soreness3Days =
    (getWellness(0)?.soreness ?? 0) > 4 &&
    (getWellness(1)?.soreness ?? 0) > 4 &&
    (getWellness(2)?.soreness ?? 0) > 4;

  // TE-05: Physical test score < 3 for 3 consecutive days
  const physicalScore3Days =
    (getWellness(0)?.physical ?? 7) < 3 &&
    (getWellness(1)?.physical ?? 7) < 3 &&
    (getWellness(2)?.physical ?? 7) < 3;

  // TE-06: Recovery score < 4 for 3 consecutive days
  const recoveryScore3Days =
    (getWellness(0)?.recovery ?? 7) < 4 &&
    (getWellness(1)?.recovery ?? 7) < 4 &&
    (getWellness(2)?.recovery ?? 7) < 4;

  // TE-10: Sleep quality < 4 for 3 consecutive days
  const sleepQuality3Days =
    (getWellness(0)?.sleep ?? 7) < 4 &&
    (getWellness(1)?.sleep ?? 7) < 4 &&
    (getWellness(2)?.sleep ?? 7) < 4;

  // TE-11: Mood < 4 for 3 consecutive days
  const mood3Days =
    (getWellness(0)?.mood ?? 7) < 4 &&
    (getWellness(1)?.mood ?? 7) < 4 &&
    (getWellness(2)?.mood ?? 7) < 4;

  // TE-12: Stress < 4 for 3 consecutive days
  const stress3Days =
    (getWellness(0)?.stress ?? 7) < 4 &&
    (getWellness(1)?.stress ?? 7) < 4 &&
    (getWellness(2)?.stress ?? 7) < 4;

  // TE-13: Health < 4 for 3 consecutive days
  const health3Days =
    (getWellness(0)?.health ?? 7) < 4 &&
    (getWellness(1)?.health ?? 7) < 4 &&
    (getWellness(2)?.health ?? 7) < 4;

  // TE-14: Avg load last 7 days < 75% of avg load last 28 days
  const last7Loads = sorted.slice(0, 7)
    .flatMap((p) => p.trainingLogs?.map((t) => t.load ?? 0) ?? [])
    .filter((l) => l > 0);
  const avg7Load = last7Loads.length > 0
    ? last7Loads.reduce((a, b) => a + b, 0) / last7Loads.length
    : 0;
  const loadBelowBaseline = avg28Load > 0 && avg7Load < 0.75 * avg28Load;

  // TE-15: HLR declining 4 consecutive days
  const h0 = getHLR(0), h1 = getHLR(1), h2 = getHLR(2), h3 = getHLR(3);
  const hlrTrending4Days =
    h0 !== null && h1 !== null && h2 !== null && h3 !== null &&
    h0 < h1 && h1 < h2 && h2 < h3;

  // TE-16: Peak score declining 4 consecutive days
  const p0 = getPeakScore(0), p1 = getPeakScore(1), p2 = getPeakScore(2), p3 = getPeakScore(3);
  const peakScoreTrending4Days =
    p0 !== null && p1 !== null && p2 !== null && p3 !== null &&
    p0 < p1 && p1 < p2 && p2 < p3;

  // TE-17: Niggle in last 24 hours
  const hasNiggle = (todayPost?.niggleLogs?.length ?? 0) > 0;

  // Injury & sickness flags from today's wellness log.
  // isSick (feeds TE-08, "You have reported being unwell today") must read the
  // day's own "sick" checkbox, not sickFactor — sickFactor is a multi-day
  // recovery decay that stays < 1.0 for days after the box is unchecked, which
  // made TE-08 fire — with its hardcoded "today" copy — throughout the whole
  // recovery window instead of only on the day actually logged (see #185).
  const isInjured = todayWellness?.injured === true;
  const canTrain = todayWellness?.canTrainWithInjury !== false; // defaults true if not set
  const isSick = todayWellness?.sick === true;

  // Build RuleContext
  const ruleContext: RuleContext = {
    injured: isInjured,
    canTrain: isInjured ? canTrain : undefined,
    sickFactor: isSick,
    hasNiggle,
    consecutiveExtremeLoadDays,
    extremeLoadSpikeYesterday,
    daysSinceLastSession,
    soreness3Days,
    physicalScore3Days,
    recoveryScore3Days,
    sleepQuality3Days,
    mood3Days,
    stress3Days,
    health3Days,
    loadBelowBaseline,
    hlrTrending4Days,
    peakScoreTrending4Days,
    // Wire in cycle context from DailyReport
    cyclePhase: cyclePhase ?? undefined,
    isMenstruatingFemale: isFemale === true && cyclePhase === "menstruation",
  };

  // Run full firing logic — returns up to 3 prioritised alerts.
  // HLR is Peak's own derivation and is still used here.
  const alertSelection = selectAlertsToDisplay(
    workloadFactor,
    roundedScore,
    wellnessFinal,
    hlr,
    ruleContext,
  );

  // Helper functions
  const getCondition = (score: number): string => {
    if (score > 90)
      return "Excellent — Wellness signals are excellent, reflecting a high sense of personal well-being, energy, and health.";

    if (score >= 76)
      return "Very Good — Wellness is very good, reflecting a good overall response to daily habits in sleep, recovery, health, and training.";

    if (score >= 61)
      return "Good — Wellness signals are generally stable, within a normal and sustainable range.";

    if (score >= 50)
      return "Below Average — Wellness signals show emerging strain, with noticeable changes in fatigue, mood, motivation, or sleep.";

    return "Low — Wellness signals are suppressed, reflecting elevated stress, fatigue, or disrupted recovery states.";
  };

  const getScoreRange = (score: number): number => {
    if (score > 100) return 4; // Ideal / PB potential
    if (score >= 80) return 3; // Excellent readiness
    if (score >= 60) return 2; // Good readiness
    if (score >= 30) return 1; // Average readiness
    return 0; // Low readiness
  };

  const peakRange = getScoreRange(roundedScore);
  // Use today's raw wellness score for labelling and AI context.
  // wellnessFinal (resilience-adjusted) is used for rule matching only.
  const rawWellnessScore = Math.round(todayWellness?.wellnessScore ?? wellnessFinal);
  const wellnessScore = rawWellnessScore;
  const wellnessCondition = getCondition(wellnessScore);
  const wellnessRange = getScoreRange(wellnessScore);

  // HLR → human-readable physiological recovery label (no raw number exposed to AI)
  const getHLRLabel = (v: number): string => {
    if (v > 1.05) return "Strong — body adapting well";
    if (v >= 1.00) return "Good — recovery on track";
    if (v >= 0.95) return "Moderate — some recovery stress present";
    if (v >= 0.90) return "Below par — recovery under stress";
    return "Low — significant recovery deficit";
  };
  const hlrLabel = getHLRLabel(hlr);

  // Create simplified data structures for explainer functions
  const simplifiedTrainingData: TrainingData[] = dayPosts.map((post) => ({
    dateString: post.dateID,
    trainingLogs: post.trainingLogs,
  }));

  const simplifiedHealthData: HealthData[] = dayPosts.map((post) => ({
    dateString: post.dateID,
    healthLogs: post.healthLogs,
  }));

  const simplifiedWellnessData: WellnessData[] = dayPosts.map((post) => ({
    dateString: post.dateID,
    wellnessLogs: post.wellnessLogs,
  }));

  // Identify the 2 weakest wellness sub-categories based on TODAY's scores (not 7-day avg)
  const wellnessMetricKeys = ["sleep", "stress", "mood", "health", "physical", "recovery"] as const;
  const wellnessMetricNames: Record<string, string> = {
    sleep: "Sleep Quality", stress: "Stress Management", mood: "Mood",
    health: "Health", physical: "Physical Readiness", recovery: "Recovery",
  };
  const todayWellnessLog = todayWellness; // WellnessLog | undefined
  const weakestWellnessMetrics = todayWellnessLog
    ? (() => {
        const scores = wellnessMetricKeys.map((key) => ({
          key,
          name: wellnessMetricNames[key],
          score: (todayWellnessLog as any)[key] as number ?? 0,
        }));
        return scores.sort((a, b) => a.score - b.score).slice(0, 2);
      })()
    : null;

  // 7-day averages per metric (used for sleep improvement context)
  const allRecentWellnessLogs = simplifiedWellnessData.flatMap((d) => d.wellnessLogs);
  const sevenDayMetricAvg = (key: string): number | null => {
    const vals = allRecentWellnessLogs.map((l) => (l as any)[key] as number).filter((v) => v > 0);
    return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  };

  const simplifiedRecoveryData: RecoveryData[] = dayPosts.map((post) => ({
    dateString: post.dateID,
    recoveryLogs: post.recoveryLogs,
  }));

  const simplifiedSleepData: SleepData[] = dayPosts.map((post) => ({
    dateString: post.dateID,
    healthLogs: post.healthLogs,
    wellnessLogs: post.wellnessLogs,
  }));

  const simplifiedNiggleData: NiggleData[] = dayPosts.map((post) => ({
    dateString: post.dateID,
    niggleLogs: post.niggleLogs,
    wellnessLogs: post.wellnessLogs,
  }));

  const healthIssuesSection = healthIssuesExplainer(simplifiedNiggleData);

  // Dynamic factors (have real values that impact peak score)
  const dynamicFactors = [
    {
      name: "workload",
      value: workloadFactor,
      description: workloadExplainer(workloadFactor, simplifiedTrainingData),
    },
    {
      name: "physiological recovery",
      value: hlr,
      description: HLRExplainer(hlr, simplifiedHealthData, excludeGarmin),
    },
  ];

  if (sickFactor !== 1.0) {
    dynamicFactors.push({
      name: "sick factor",
      value: sickFactor,
      description: sickExplainer(sickFactor, simplifiedWellnessData, todayDateID),
    });
  }

  // Supporting factors (no real factor value, always shown after dynamic factors)
  const supportingFactors = [
    {
      name: "recovery",
      description: recoveryExplainer(simplifiedRecoveryData),
    },
    {
      name: "sleep",
      description: sleepExplainer(simplifiedSleepData, false, sleepGoalHours, excludeGarmin),
    },
  ];

  // Generate detailed interpretations with sections
  const generateDetailedInterpretation = (simpleMode = false): string => {
    // Wellness explanation using the wellnessExplainer function
    const wellnessExplanation = `## Wellness Explained\n${wellnessExplainer(
      wellnessScore,
      wellnessCondition,
      peakRange,
      wellnessRange,
      simplifiedWellnessData,
    )}.`;

    // Sort dynamic factors by most impactful first (furthest from 1.0)
    const orderedDynamicFactors = [...dynamicFactors].sort(
      (a, b) => Math.abs(b.value - 1.0) - Math.abs(a.value - 1.0),
    );

    // Generate factor sections: dynamic factors first (most impactful), then supporting factors
    const dynamicSections = orderedDynamicFactors
      .map((factor) => {
        const factorName = factor.name
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        return `## ${factorName} Analysed\n${factor.description}`;
      })
      .join("\n\n");

    const supportingSections = supportingFactors
      .map((factor) => {
        const factorName = factor.name
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
        return `## ${factorName} Analysed\n${factor.description}`;
      })
      .join("\n\n");

    if (simpleMode) {
      const allFactors = [...orderedDynamicFactors, ...supportingFactors];
      const workloadDescription =
        allFactors.find((factor: any) => factor.name === "workload")
          ?.description || "";
      const hlrDescription =
        allFactors.find((factor: any) => factor.name === "physiological recovery")?.description ||
        "";
      const sleepDescription =
        allFactors.find((factor: any) => factor.name === "sleep")
          ?.description || "";

      const workloadCategoryOnly = workloadDescription.includes(
        " Over the past 7 days",
      )
        ? workloadDescription.split(" Over the past 7 days")[0]
        : workloadDescription;

      const wellnessTopOnly = wellnessExplanation.includes("\n\n")
        ? wellnessExplanation.split("\n\n")[0]
        : wellnessExplanation;

      return [
        wellnessTopOnly,
        `## Workload Analysed\n${workloadCategoryOnly}`,
        `## Physiological Recovery Analysed\n${hlrDescription}`,
        `## Sleep Analysed\n${sleepDescription}`,
      ].join("\n\n");
    }

    // Combine all sections
    let detailedText = wellnessExplanation;
    if (dynamicSections) {
      detailedText += "\n\n" + dynamicSections;
    }
    if (supportingSections) {
      detailedText += "\n\n" + supportingSections;
    }

    return detailedText;
  };

  const detailedInterpretation = generateDetailedInterpretation(simple);

  const basicInterpretation = (() => {
    if (roundedScore > 100) {
      return `The athlete's peak score is ${roundedScore}, indicating ideal conditions for a possible personal best or near personal best performance today.`;
    }

    if (roundedScore >= 80) {
      return `The athlete's peak score is ${roundedScore}, indicating excellent readiness and training balance. This is an optimal day for high-intensity training.`;
    }

    if (roundedScore >= 60) {
      return `The athlete's peak score is ${roundedScore}, indicating good readiness. Suitable for all training, but monitor how you feel during warm up.`;
    }

    if (roundedScore >= 30) {
      return `The athlete's peak score is ${roundedScore}, indicating average readiness. There is room for improvement in wellness and training load balance. If training today, monitor warm up closely and consider reducing load or intensity.`;
    }

    return `The athlete's peak score is ${roundedScore}, indicating low readiness. Prioritise recovery and rest today to support adaptation and reduce risk.`;
  })();

  // Shared wellness overview block — included in both simple and full report paths
  const wellnessOverviewBlock = `**Today's Wellness Score**: ${rawWellnessScore} (today's snapshot — ${wellnessCondition})
**Today's Wellness Breakdown**: Sleep: ${(todayWellnessLog as any)?.sleep ?? "N/A"}/7 | Stress: ${(todayWellnessLog as any)?.stress ?? "N/A"}/7 | Mood: ${(todayWellnessLog as any)?.mood ?? "N/A"}/7 | Health: ${(todayWellnessLog as any)?.health ?? "N/A"}/7 | Physical: ${(todayWellnessLog as any)?.physical ?? "N/A"}/7 | Recovery: ${(todayWellnessLog as any)?.recovery ?? "N/A"}/7${rawWellnessScore < 75 && weakestWellnessMetrics && weakestWellnessMetrics.length > 0 ? `

⚠️ MANDATORY AI INSTRUCTION: Wellness is below 75. Your FINAL sentence MUST name and address BOTH areas below. This cannot be skipped.
**Wellness Priority — 2 areas to improve (based on today's scores)**:
1. ${weakestWellnessMetrics[0].name}: ${weakestWellnessMetrics[0].score}/7 today${weakestWellnessMetrics[0].key === "sleep" ? ` — address last night's sleep quality (see Sleep section). 7-day quality trend: ${sevenDayMetricAvg("sleep")?.toFixed(1) ?? "N/A"}/7 avg.` : ` (7-day avg: ${sevenDayMetricAvg(weakestWellnessMetrics[0].key)?.toFixed(1) ?? "N/A"}/7)`}
2. ${weakestWellnessMetrics[1]?.name ?? "N/A"}: ${weakestWellnessMetrics[1]?.score ?? "N/A"}/7 today${weakestWellnessMetrics[1]?.key === "sleep" ? ` — address last night's sleep quality (see Sleep section). 7-day quality trend: ${sevenDayMetricAvg("sleep")?.toFixed(1) ?? "N/A"}/7 avg.` : ` (7-day avg: ${sevenDayMetricAvg(weakestWellnessMetrics[1]?.key ?? "")?.toFixed(1) ?? "N/A"}/7)`}` : ""}`;

  // Build the markdown string
  let markdown = simple
    ? `# ${title}

  ## Overview of Today's Data

**Today's Peak Score**: ${roundedScore}

**Today's Physiological Recovery**: ${hlrLabel}

${wellnessOverviewBlock}`
    : `# ${title}

  ## Overview of Today's Data

**Today's Peak Score**: ${roundedScore}

**Today's Physiological Recovery**: ${hlrLabel}

**Today's WorkLoad Factor**: ${workloadFactor.toFixed(3) || "N/A"}

**Today's Sick Factor**: ${sickFactor.toFixed(3) || "N/A"}

${wellnessOverviewBlock}`;

  // Add athlete-facing condition guidance from alert selection
  const toneForTier = (tier: string): string => {
    switch (tier.toLowerCase()) {
      case "green": return "Use an encouraging and positive tone. Reinforce what is working and give confidence for today's training.";
      case "amber": return "Use a cautionary but constructive tone. Highlight warning signs and suggest sensible adjustments.";
      case "red":   return "Use a direct and urgent but supportive tone. Emphasise immediate action to reduce risk and recover.";
      default:      return "Use a clear, supportive tone focused on practical next steps for today.";
    }
  };

  if (alertSelection.alerts.length > 0) {
    markdown += `

## Athletes Condition to tell the Athlete

**Display Combination**: ${alertSelection.displayCombination}
**Overall Tone**: ${alertSelection.primaryTone}

The following ${alertSelection.alerts.length} alert(s) have been selected for display in priority order. You MUST address each one in your response, starting with Alert 1.

${alertSelection.alerts.map((alert, i) => `### Alert ${i + 1} — ${alert.alertTier} (${alert.alertId}: ${alert.alertName})

**Rule Insight**: ${alert.athleteInsight}
${alert.meaning ? `**Meaning**: ${alert.meaning}` : ""}
**Primary Driver**: ${(alert.primaryTriggerDriver || "General").replace(/\bHLR\b/gi, "Physiological Recovery")}
**Secondary Driver**: ${(alert.secondaryTriggerDriver || "None").replace(/\bHLR\b/gi, "Physiological Recovery")}
**Tone**: ${toneForTier(alert.alertTier)}`).join("\n\n")}

**IMPORTANT**: Your response must address all ${alertSelection.alerts.length} alert(s) above. Begin with Alert 1 as your highest priority. If the primary tone is Red, your opening sentence must reflect urgency and care.`;

  } else if (!simple) {
    markdown += `

## Peak AI Rule Analysis

No specific Peak AI rules matched your current data combination. Using general readiness guidelines.`;
  } else {
    markdown += `

## Athletes Condition to tell the Athlete

No specific Peak AI rules matched today's data. Use a clear, supportive tone with practical next steps for today.

**General guidance**: ${basicInterpretation}`;
  }

  if (simple) {
    markdown += `

${detailedInterpretation}`;

    markdown += `\n\n${healthIssuesSection}`;

    if (recentAIInsights && Object.keys(recentAIInsights).length > 0) {
      markdown += `\n\n${buildAIInsightsSection(recentAIInsights)}`;
    }

    return markdown;
  }

  markdown += `


## Peak Score Breakdown

**${basicInterpretation}**

The Peak Score formula is: **wellnessFinal × √HLR × workloadFactor × sickFactor × sorenessMultiplier − injuryPenalty**

**Factor importance — use this to weight your commentary:**
1. **Wellness (PRIMARY)** — directly multiplied; largest single driver of the peak score. Today's raw wellness score is the snapshot of how the athlete feels.
2. **Load Balance (PRIMARY)** — directly multiplied; compares acute 7-day training load to chronic 28-day average. The closer to balance, the better the score.
3. **Physiological Recovery / HLR (MINOR)** — applied as √HLR. Because HLR typically sits close to 1.0, its square root contributes only ±2–5% to the final score. Do NOT describe HLR as a "significant" or "major" driver. It is a supporting signal only.
4. **Sick Factor (PENALTY)** — reduces score when the athlete is sick. Only relevant when active.
5. **Soreness Multiplier (PENALTY)** — reduces score based on soreness. Only relevant when elevated.
6. **Injury Penalty (PENALTY)** — subtracted directly. Only relevant when an injury is logged.

**Load Balance (workloadFactor)** compares how much training an athlete has done recently (7-day acute load) to what their body has been prepared for over time (28-day chronic load). If the daily average load for the last 7 days is less than the last 28 days, workloadFactor positively impacts peakScore. If the last 7-day average is greater, it negatively impacts peakScore.

**Wellness (WellnessFinal)** is the athlete's subjective wellness score today adjusted by how their 7-day wellness compares to their 28-day wellness baseline. Measured via: Sleep Duration, Sleep Quality, Stress Management, Mood, Health, Physical Readiness & Recovery. Recovery is double weighted.

**Physiological Recovery (HLR)** compares the last 3-day acute average of Resting Heart Rate, HRV, and Respiration Rate against the 28-day baseline. Applied as square root, so its practical impact on the peak score is small. It is a useful supporting signal but should never be framed as a primary driver.

${detailedInterpretation}`;

  markdown += `

## Additional Notes if necessary

Don't include values of Load Balance, Wellness or Physiological Recovery in the insights, just the associated text interpretations.
Reference Load Balance as Training Load, wellnessFinal as Wellness, and HLR as Physiological Recovery — NEVER use the term "HLR" in the athlete-facing insight.

Don't give the alert rule number or colour to the athlete. Instead, use the traffic light colour to determine the tone of the insights.

`;

  markdown += `\n\n${healthIssuesSection}\n`;

  if (recentAIInsights && Object.keys(recentAIInsights).length > 0) {
    markdown += `\n${buildAIInsightsSection(recentAIInsights)}\n`;
  }

  return markdown;
};
